import _ from 'underscore';
import PropTypes from 'prop-types';
import { useFormState } from 'react-use-form-state';
import React, { useEffect, useRef, useState } from 'react';
import { addHours, addMinutes } from 'date-fns';

import { convertToTimeZone } from 'date-fns-timezone';

import {
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';

import { Button } from '@shoflo/polymer/components/Button/Button';
import StyledFormLabel from 'components/form/StyledFormLabel';
import Tooltip from '../Tooltip';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
  ActionButtonWrapper,
  StyledAsteriskSpan,
  StyledDurationSpan,
} from './Form.styled';
import {
  calculateDuration,
  dateFieldsAreValid,
  getRoundedDate,
  getTimeZonedDate,
} from './helper/Form.helper';
import { EndDateTimePickerWithToolTip } from './DateTimePicker/EndDateTimePickerWithToolTip';
import { StartDateTimePicker } from './DateTimePicker/StartDateTimePicker';

const Asterisk = () => {
  return <StyledAsteriskSpan>*</StyledAsteriskSpan>;
};

const CreateForm = ({ broadcast, onSubmit, onCancel, event }) => {
  const titleRef = useRef();

  useEffect(() => {
    titleRef.current.focus();
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialState = broadcast
    ? {
        title: broadcast.title,
        description: broadcast.description,
      }
    : { title: '', description: '' };

  const [formState, { text }] = useFormState(initialState);

  const { validity, errors } = formState;
  const notSubmittingButtonText = broadcast ? 'Save' : 'Create';
  const submittingButtonText = broadcast ? 'Saving...' : 'Creating...';
  const submitButtonText = isSubmitting
    ? submittingButtonText
    : notSubmittingButtonText;
  const titleLessThan3Chars = 'title less than 3';
  const titleGreaterThan100Chars = 'title greater than 100';

  const timeZone = event.timeZone;

  const [minEndDate, setMinEndDate] = useState(undefined);
  const [maxEndDate, setMaxEndDate] = useState(undefined);
  const [startDateTime, setStartDateTime] = useState(() =>
    getStartDateInitialState(
      broadcast,
      event,
      setMinEndDate,
      setMaxEndDate,
      timeZone,
    ),
  );
  const [duration, setDuration] = useState('--:--');
  const [endDateTime, setEndDateTime] = useState(() =>
    getEndDateInitialState(broadcast, setDuration, startDateTime, timeZone),
  );
  const [showStartDateError, setShowStartDateError] = useState(false);
  const [showEndDateError, setShowEndDateError] = useState(false);

  const dateFormat = 'MMMM d, yyyy';
  const use12Hours = !event.twentyFourHourClock;
  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isEventAndSystemTimeZoneSame = timeZone === systemTimeZone;

  const handleStartDateChange = (date) => {
    if (date) {
      date.setSeconds(0, 0);
      date = getRoundedDate(15, date);
      setStartDateTime(date);
      setMinEndDate(new Date(addMinutes(date, 15)));
      setMaxEndDate(new Date(addHours(date, 25)));
      const endDate = new Date(addHours(date, 1));
      setEndDateTime(endDate);
      setDuration(calculateDuration(date, endDate));
      setShowStartDateError(false);
    } else {
      setStartDateTime(undefined);
      setEndDateTime(undefined);
      broadcast ? setShowStartDateError(false) : setShowStartDateError(true);
      setDuration('--:--');
    }
    setShowEndDateError(false);
  };

  const handleEndDateChange = (date) => {
    if (date) {
      date.setSeconds(0, 0);
      date = getRoundedDate(15, date);
      if (date < minEndDate) {
        date.setDate(minEndDate.getDate());
        date.setMonth(minEndDate.getMonth());
        if (date < minEndDate) date.setTime(minEndDate.getTime());
      }
      if (date > maxEndDate) {
        date.setHours(maxEndDate.getHours());
        date.setMinutes(maxEndDate.getMinutes());
      }
    }
    setEndDateTime(date);
    setDuration(calculateDuration(startDateTime, date));
    date ? setShowEndDateError(false) : setShowEndDateError(true);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const broadcast = _.pick(formState.values, 'title', 'description');

        if (startDateTime && endDateTime) {
          const startTimeZonedDate = getTimeZonedDate(timeZone, startDateTime);
          const endTimeZonedDate = getTimeZonedDate(timeZone, endDateTime);

          broadcast.startOn = startTimeZonedDate.getTime();
          broadcast.endOn = endTimeZonedDate.getTime();
        } else {
          broadcast.startOn = 0;
          broadcast.endOn = 0;
        }

        onSubmit(broadcast);
      }}
    >
      <FormGroup>
        <Asterisk />
        <StyledFormLabel id="titleLabel" htmlFor="broadcastTitle">
          Name
        </StyledFormLabel>
        <Input
          data-testid="nameInput"
          id="broadcastTitle"
          placeholder="e.g. GS Day One"
          {...text({
            name: 'title',
            validate: (t) => {
              return t.length < 3
                ? titleLessThan3Chars
                : t.length > 100
                ? titleGreaterThan100Chars
                : null;
            },
            touchOnChange: true,
          })}
          innerRef={titleRef}
          invalid={formState.touched.title && !formState.validity.title}
        />
        {validity.title === false && (
          <FormFeedback>
            {errors.title === titleLessThan3Chars
              ? 'Uh oh! Studio names must be at least 3 characters.'
              : errors.title === titleGreaterThan100Chars
              ? 'Uh oh!  You are over the character limit of 100 characters.'
              : null}
          </FormFeedback>
        )}
      </FormGroup>

      <FormGroup>
        <StyledFormLabel id="descriptionLabel" htmlFor="broadcastDescription">
          Description
        </StyledFormLabel>
        <Input
          data-testid="descriptionInput"
          id="broadcastDescription"
          aria-labelledby="descriptionLabel"
          {...text({
            name: 'description',
            validate: (t) => !(t.length && t.length > 100),
            touchOnChange: true,
          })}
          placeholder="e.g. Main Ballroom"
          invalid={
            formState.touched.description && !formState.validity.description
          }
        />
        {validity.description === false && (
          <FormFeedback>
            Uh oh! You are over the character limit of 100 characters.
          </FormFeedback>
        )}
      </FormGroup>

      <Row form>
        <Col md={6}>
          <FormGroup>
            {!broadcast && <Asterisk />}
            <StyledFormLabel
              htmlFor="broadcastStartDateTime"
              data-testid="startDateTimeInputLabel"
              id="startDateTimeLabel"
              aria-describedby="tip-broadcast-form-start-date-time"
            >
              Start date and time
            </StyledFormLabel>
            {!isEventAndSystemTimeZoneSame && (
              <FontAwesomeIcon
                id="startDateTimeInfo"
                className="ms-2"
                size="xs"
                color="#767676"
                icon={faInfoCircle}
              />
            )}
            {!isEventAndSystemTimeZoneSame && (
              <UncontrolledTooltip placement="top" target="startDateTimeInfo">
                This time is based on the Event&apos;s timezone
              </UncontrolledTooltip>
            )}
            <Tooltip
              id="broadcast-form-start-date-time"
              title="When is this broadcast scheduled to begin"
              placement="top"
            />
            <StartDateTimePicker
              broadcast={broadcast}
              startDateTime={startDateTime}
              dateFormat={dateFormat}
              handleStartDateChange={handleStartDateChange}
              use12Hours={use12Hours}
              showStartDateError={showStartDateError}
            />
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            {!broadcast && <Asterisk />}
            <StyledFormLabel
              htmlFor="broadcastEndDateTime"
              data-testid="endDateTimeInputLabel"
              id="endDateTimeLabel"
              aria-describedby="tip-broadcast-form-end-date-time"
            >
              End date and time
            </StyledFormLabel>
            {!isEventAndSystemTimeZoneSame && (
              <FontAwesomeIcon
                id="endDateTimeInfo"
                className="ms-2"
                size="xs"
                color="#767676"
                icon={faInfoCircle}
              />
            )}
            {!isEventAndSystemTimeZoneSame && (
              <UncontrolledTooltip placement="top" target="endDateTimeInfo">
                This time is based on the Event&apos;s timezone
              </UncontrolledTooltip>
            )}
            <Tooltip
              id="broadcast-form-end-date-time"
              title="This will not auto terminate your studio stream"
              placement="top"
            />
            <EndDateTimePickerWithToolTip
              broadcast={broadcast}
              endDateTime={endDateTime}
              dateFormat={dateFormat}
              handleEndDateChange={handleEndDateChange}
              use12Hours={use12Hours}
              startDateTime={startDateTime}
              minEndDate={minEndDate}
              maxEndDate={maxEndDate}
              setShowEndDateError={setShowEndDateError}
              showEndDateError={showEndDateError}
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <StyledFormLabel
          id="broadcastDuration"
          data-testid="durationLabel"
          aria-describedby="tip-broadcast-form-duration"
        >
          Duration
        </StyledFormLabel>
        <Tooltip
          id="broadcast-form-duration"
          title="Calculated based on start and end times"
          placement="right"
        />
        <StyledDurationSpan
          id="broadcastDurationValue"
          data-testid="durationValue"
        >
          {duration}
        </StyledDurationSpan>
      </FormGroup>

      <ActionButtonWrapper className="modal-footer">
        <Button
          data-testid="saveButton"
          color="primary"
          disabled={
            !formIsValid(formState) ||
            isSubmitting ||
            dateFieldsAreValid(broadcast, startDateTime, endDateTime)
          }
        >
          {submitButtonText}
        </Button>{' '}
        <Button
          data-testid="cancelButton"
          color="secondary"
          variant="outlined"
          onClick={(e) => {
            e.preventDefault();
            onCancel();
          }}
        >
          Cancel
        </Button>
      </ActionButtonWrapper>
    </Form>
  );
};

const formIsValid = (formState) => {
  /**
   * 1. Check if any field is invalid
   * 2. Check if any field is touched
   */
  return (
    _.some(formState.validity, (v) => v) && _.some(formState.touched, (t) => t)
  );
};

const getStartDateInitialState = (
  broadcast,
  event,
  setMinEndDate,
  setMaxEndDate,
  timeZone,
) => {
  let startDate;
  if (broadcast) {
    if (broadcast.startOn) {
      startDate = convertToTimeZone(new Date(broadcast.startOn), {
        timeZone: timeZone,
      });
    }
  } else {
    const currentDate = convertToTimeZone(new Date(), { timeZone: timeZone });
    const eventStartDate = convertToTimeZone(new Date(event.startOn), {
      timeZone: timeZone,
    });
    if (eventStartDate > currentDate) {
      startDate = eventStartDate;
      startDate.setHours(8);
      startDate.setMinutes(0);
    } else {
      startDate = convertToTimeZone(getRoundedDate(15, new Date()), {
        timeZone: timeZone,
      });
    }
  }
  setMinEndDate(new Date(addMinutes(startDate, 15)));
  setMaxEndDate(new Date(addHours(startDate, 25)));
  return startDate;
};

const getEndDateInitialState = (
  broadcast,
  setDuration,
  startDateTime,
  timeZone,
) => {
  let endDate;
  if (broadcast && broadcast.endOn) {
    endDate = convertToTimeZone(new Date(broadcast.endOn), {
      timeZone: timeZone,
    });
    setDuration(calculateDuration(startDateTime, endDate));
  }
  return endDate;
};

CreateForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  event: PropTypes.object.isRequired,
};

export default CreateForm;
